/**
 * Cancelar y guardar en un formulario **pintado por el servidor**.
 *
 * El constructor de formularios ya resuelve las dos cosas para los que arma él
 * —pregunta antes de descartar y deja el botón en espera— pero la mitad de las
 * pantallas de un proyecto son un `<form method="post">` escrito en la
 * plantilla, y ahí no llegaba nada: Cancelar era un enlace suelto que se lleva
 * por delante lo escrito de un clic, y Guardar se podía pulsar tres veces
 * seguidas mientras el servidor contestaba.
 *
 * Que se comporten distinto según cómo esté hecho el formulario por dentro es
 * exactamente lo que un theme existe para evitar: quien lo usa no sabe —ni tiene
 * por qué saber— cuál de los dos está mirando.
 *
 * ## Se pide, no se impone
 *
 * Va por atributo y no sobre todo `<form>` que haya en la página. Un listado
 * lleno de formularios de una línea —«activar», «dar de baja»— no quiere ni
 * diálogo ni botón bloqueado, y encendérselo a todos convertiría una mejora en
 * una pantalla que pregunta cada vez que se pulsa algo.
 *
 * ```html
 * <form method="post" data-company-form-guard>
 *   …
 *   <div class="page-footer">
 *     <a class="btn btn-outline-primary" href="/volver" data-company-cancel>Cancelar</a>
 *     <button type="submit" class="btn btn-primary">Guardar</button>
 *   </div>
 * </form>
 * ```
 *
 * ## Aquí no hay `done()` ni `fail()`
 *
 * Y no se echan de menos: un formulario del servidor **navega**. Si guarda,
 * llega otra página; si el servidor lo rechaza, llega la misma con los errores
 * pintados y el botón recién nacido. Lo que en el constructor hay que devolver a
 * mano, aquí lo devuelve la recarga.
 *
 * El único final que no navega es que la petición no salga —red caída, o el
 * `submit` cancelado por otro guion—, y para eso está el respaldo por tiempo:
 * ver {@link RESPALDO}.
 */

import { confirm } from './dialog.js';
import { formES } from './locale-es.js';

const MARCA = 'data-company-form-guard';
const YA_MONTADO = '__companyFormGuard';

/**
 * Cuánto se espera antes de devolver el botón si la página no se ha ido.
 *
 * Un formulario del servidor navega, así que normalmente esto no llega a
 * dispararse: la página desaparece antes. Está para el caso en que la petición
 * ni siquiera sale —sin red, o alguien canceló el envío— porque un botón
 * bloqueado para siempre es peor que uno que se puede pulsar dos veces: deja la
 * pantalla sin salida y obliga a recargar perdiendo lo escrito.
 *
 * Quince segundos: más que cualquier guardado normal y menos que la paciencia
 * de quien mira.
 */
const RESPALDO = 15000;

export function setupFormGuards(root = document) {
  const alcance = root && root.querySelectorAll ? root : document;

  alcance.querySelectorAll(`form[${MARCA}]`).forEach((form) => {
    if (form[YA_MONTADO]) return;

    form[YA_MONTADO] = new FormGuard(form);
  });
}

class FormGuard {
  constructor(form) {
    this.form = form;
    this.busy = false;
    this.temporizador = null;

    this.submitButton = form.querySelector('[type="submit"]');
    this.cancelLink = form.querySelector('[data-company-cancel]')
      // Sin marcar también vale: en la práctica el de cancelar es el enlace del
      // pie, y pedir un atributo más para lo de siempre es pedir que se olvide.
      || form.querySelector('.page-footer a[href], .form-actions a[href]');

    // Lo escrito al abrir. Comparar contra esto es lo que distingue «me
    // arrepentí sin tocar nada» de «llevo diez campos puestos», y sin esa
    // distinción el diálogo sale siempre y enseña a confirmar sin leer.
    this.inicial = this.instantanea();

    form.addEventListener('submit', this.alEnviar.bind(this));

    if (this.cancelLink) {
      this.cancelLink.addEventListener('click', this.alCancelar.bind(this));
    }
  }

  /**
   * Lo que hay escrito ahora, como texto comparable.
   *
   * Con `FormData` y no leyendo campo a campo: entiende los mismos controles que
   * va a enviar el navegador —casillas, radios, selects múltiples— y así lo que
   * se compara es exactamente lo que se mandaría.
   *
   * Los ficheros se cuentan por su nombre: el contenido no se puede comparar sin
   * leerlo entero, y para saber si alguien eligió un PDF basta con que aparezca.
   */
  instantanea() {
    const datos = new FormData(this.form);
    const partes = [];

    datos.forEach((valor, clave) => {
      partes.push(`${clave}=${valor instanceof File ? valor.name : valor}`);
    });

    return partes.join('&');
  }

  sucio() {
    return this.instantanea() !== this.inicial;
  }

  alEnviar(evento) {
    // Ya se está enviando. Un segundo clic —o un Enter en cualquier campo, que
    // el `disabled` del botón no frena— mandaría el formulario otra vez, y eso
    // son dos registros iguales que alguien tiene que borrar a mano.
    if (this.busy) {
      evento.preventDefault();
      return;
    }

    this.enEspera(true);
  }

  alCancelar(evento) {
    // A mitad de un guardado no se cancela: la petición ya salió y el registro
    // puede estar creándose. Irse ahora deja a quien mira creyendo que no se
    // guardó nada cuando el servidor ya lo tiene.
    if (this.busy) {
      evento.preventDefault();
      return;
    }

    if (!this.sucio()) return;

    evento.preventDefault();

    const destino = this.cancelLink.getAttribute('href');

    confirm({
      type: 'warning',
      title: formES.cancelTitle,
      message: formES.cancelMessage,
      confirmText: formES.cancelConfirm,
      cancelText: formES.cancelKeep,
    }).then((ok) => {
      // El botón del diálogo no es rojo: quien cancela ya decidió irse. El rojo
      // se reserva para lo que destruye algo que ya existía.
      if (ok && destino) window.location.href = destino;
    });
  }

  enEspera(on) {
    this.busy = !!on;

    if (this.cancelLink) {
      this.cancelLink.classList.toggle('disabled', this.busy);
      this.cancelLink.setAttribute('aria-disabled', this.busy ? 'true' : 'false');
    }

    if (!this.submitButton) return;

    this.submitButton.disabled = this.busy;
    this.submitButton.setAttribute('aria-busy', this.busy ? 'true' : 'false');

    if (!this.busy) {
      if (this.spinner) {
        this.spinner.remove();
        this.spinner = null;
      }
      if (this.rotulo !== undefined) this.submitButton.textContent = this.rotulo;
      return;
    }

    this.rotulo = this.submitButton.textContent;

    this.spinner = document.createElement('span');
    this.spinner.className = 'spinner-border spinner-border-sm company-btn-spinner';
    this.spinner.setAttribute('role', 'status');
    this.spinner.setAttribute('aria-hidden', 'true');

    // El rótulo cambia además de la rueda: dice QUÉ está pasando, y es lo que
    // lee quien va con lector de pantalla, que no ve girar nada.
    this.submitButton.textContent = ` ${formES.saving}`;
    this.submitButton.insertBefore(this.spinner, this.submitButton.firstChild);

    clearTimeout(this.temporizador);
    this.temporizador = setTimeout(() => this.enEspera(false), RESPALDO);
  }
}
